import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useUpdateIdAndPay } from 'features/loyalty-user/use-update-id-and-pay';
import { usePaymentContext } from 'features/payment/quick-pay';
import { IPaymentMethod } from 'features/payment/types';
import {
  getPaymentMethodId,
  isAllowedQuickPayMethod,
  isCreditCardNotExpired,
} from 'features/payment/utils';

import { useAuthContext } from 'state/auth';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser, useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { toError } from 'utils/errors';
import logger from 'utils/logger';

// Validating expiration date for the current credit card
const isCurrentPaymentMethodValid = (
  availablePaymentMethods: IPaymentMethod[],
  selectedPaymentMethod?: string | null
) => {
  const currentPayment = availablePaymentMethods.find(
    item => getPaymentMethodId(item) === selectedPaymentMethod
  );

  return !!currentPayment && isCreditCardNotExpired(currentPayment?.credit);
};

export const useQuickPayState = () => {
  const {
    paymentMethods,
    recentlyAddedQuickPayMethodId,
    setRecentlyAddedQuickPayMethodId,
  } = usePaymentContext();

  const availablePaymentMethods = useMemo(
    () => paymentMethods.filter(paymentMethod => isAllowedQuickPayMethod(paymentMethod)),
    [paymentMethods]
  );

  const [initializedLoyaltyId, setInitializedLoyaltyId] = useState<string>();
  // Tracks one missing-`idPay` seed mutation per identity.
  const seededLoyaltyIdRef = useRef<string | undefined>(undefined);
  const [selectedMethod, setSelectedMethod] = useState<string | undefined>();
  const { isQuickPayEnabled, setIsQuickPayEnabled } = useInRestaurantRedemptionContext();

  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const { loyaltyUser, loading: loyaltyUserLoading } = useLoyaltyUser();
  const { refetchLoyaltyUser } = useLoyaltyContext();
  const { user } = useAuthContext();
  const { updateIdAndPay } = useUpdateIdAndPay();
  const [revalidatedLoyaltyId, setRevalidatedLoyaltyId] = useState<string>();
  const profileRevalidated = Boolean(loyaltyId && revalidatedLoyaltyId === loyaltyId);
  const profileRefreshPending = Boolean(loyaltyId && !profileRevalidated);
  const loading = loyaltyUserLoading || profileRefreshPending;
  const idPay = profileRevalidated ? loyaltyUser?.idPay : undefined;
  const isQuickPayAvailable = availablePaymentMethods.length > 0 && !!idPay;

  const persistIdAndPay = useCallback(
    (input: Parameters<typeof updateIdAndPay>[0]) => {
      void updateIdAndPay(input).catch(error => {
        logger.error({
          error: toError(error),
          message: 'Error updating Quick Pay settings',
        });
      });
    },
    [updateIdAndPay]
  );

  useEffect(() => {
    if (!loyaltyId) {
      setRevalidatedLoyaltyId(undefined);
      setInitializedLoyaltyId(undefined);
      setSelectedMethod(undefined);
      setIsQuickPayEnabled(false);
      seededLoyaltyIdRef.current = undefined;
      return;
    }
    if (revalidatedLoyaltyId === loyaltyId) {
      return;
    }

    setInitializedLoyaltyId(undefined);
    setSelectedMethod(undefined);
    setIsQuickPayEnabled(false);
    seededLoyaltyIdRef.current = undefined;

    const requestedLoyaltyId = loyaltyId;
    let isCurrentRequest = true;
    void refetchLoyaltyUser()
      .then(refreshedLoyaltyUser => {
        const refreshedCurrentIdentity =
          refreshedLoyaltyUser === null || refreshedLoyaltyUser?.id === requestedLoyaltyId;
        if (isCurrentRequest && refreshedCurrentIdentity) {
          setRevalidatedLoyaltyId(requestedLoyaltyId);
        }
      })
      .catch(error => {
        logger.error({
          error: toError(error),
          message: 'Error refreshing loyalty user for Quick Pay',
        });
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [loyaltyId, refetchLoyaltyUser, revalidatedLoyaltyId, setIsQuickPayEnabled]);

  useEffect(() => {
    if (!profileRevalidated || initializedLoyaltyId === loyaltyId || !idPay || !loyaltyId) {
      return;
    }
    const isSelectedMethodAvailable = availablePaymentMethods.some(
      method => getPaymentMethodId(method) === idPay.storedPaymentMethodId
    );

    setIsQuickPayEnabled(Boolean(idPay.enabled && isSelectedMethodAvailable));
    if (idPay.storedPaymentMethodId === undefined || idPay.storedPaymentMethodId === null) {
      setSelectedMethod(
        availablePaymentMethods.length
          ? getPaymentMethodId(availablePaymentMethods[availablePaymentMethods.length - 1])
          : undefined
      );
    } else if (!isSelectedMethodAvailable) {
      // The user had a payment method selected and it is not available anymore.
      setSelectedMethod(
        availablePaymentMethods.length
          ? getPaymentMethodId(availablePaymentMethods[availablePaymentMethods.length - 1])
          : undefined
      );
    } else {
      setSelectedMethod(idPay.storedPaymentMethodId);
    }
    setInitializedLoyaltyId(loyaltyId);
  }, [
    idPay,
    initializedLoyaltyId,
    loyaltyId,
    profileRevalidated,
    availablePaymentMethods,
    setIsQuickPayEnabled,
  ]);

  useEffect(() => {
    if (
      !profileRevalidated ||
      initializedLoyaltyId !== loyaltyId ||
      !recentlyAddedQuickPayMethodId
    ) {
      return;
    }

    const addedMethodIsAvailable = availablePaymentMethods.some(
      method => getPaymentMethodId(method) === recentlyAddedQuickPayMethodId
    );

    if (!addedMethodIsAvailable) {
      return;
    }

    setSelectedMethod(recentlyAddedQuickPayMethodId);
    setIsQuickPayEnabled(true);
    setRecentlyAddedQuickPayMethodId(undefined);
  }, [
    availablePaymentMethods,
    initializedLoyaltyId,
    loyaltyId,
    profileRevalidated,
    recentlyAddedQuickPayMethodId,
    setIsQuickPayEnabled,
    setRecentlyAddedQuickPayMethodId,
  ]);

  useEffect(() => {
    if (!profileRevalidated || !loyaltyId) {
      return;
    }

    if (!idPay) {
      // No Id & Pay row on the loyalty user yet: persist a default so the API returns `idPay`.
      // We use the most recently added eligible card with quick pay off; the init effect above
      // can then align local toggle/selection once `idPay` exists.
      if (
        seededLoyaltyIdRef.current !== loyaltyId &&
        !loading &&
        loyaltyUser &&
        availablePaymentMethods.length > 0
      ) {
        seededLoyaltyIdRef.current = loyaltyId;
        persistIdAndPay({
          storedPaymentMethodId: availablePaymentMethods.length
            ? getPaymentMethodId(availablePaymentMethods[availablePaymentMethods.length - 1])
            : undefined,
          enabled: false,
        });
      }
      return;
    }
    if (initializedLoyaltyId !== loyaltyId) {
      return;
    }
    if (
      (idPay.storedPaymentMethodId !== selectedMethod || idPay.enabled !== isQuickPayEnabled) &&
      isCurrentPaymentMethodValid(availablePaymentMethods, selectedMethod)
    ) {
      persistIdAndPay({
        storedPaymentMethodId: selectedMethod,
        enabled: isQuickPayEnabled,
      });
    }
    if (!isQuickPayAvailable && idPay.storedPaymentMethodId) {
      persistIdAndPay({
        storedPaymentMethodId: undefined,
        enabled: false,
      });
    }
  }, [
    idPay,
    selectedMethod,
    availablePaymentMethods,
    persistIdAndPay,
    loyaltyId,
    loading,
    loyaltyUser,
    isQuickPayEnabled,
    isQuickPayAvailable,
    initializedLoyaltyId,
    profileRevalidated,
  ]);

  const handleToggle = () => {
    if (isCurrentPaymentMethodValid(availablePaymentMethods, selectedMethod)) {
      logger.info({
        message: `[INFO] Quick Pay Toggle`,
        loyaltyId: user?.loyaltyId,
        toggleStatus: !isQuickPayEnabled,
      });
    }
    setIsQuickPayEnabled(isEnabled => !isEnabled);
  };

  const handleSelection = (fdAccountId: string | undefined) => {
    setSelectedMethod(fdAccountId);
  };

  return {
    isQuickPayEnabled,
    selectedMethod,
    availablePaymentMethods,
    isQuickPayAvailable,
    loading,
    handleToggle,
    handleSelection,
  };
};
